import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, withModifiers as _withModifiers, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "c-download-resource__panel" }
const _hoisted_2 = { class: "c-download-resource__header" }
const _hoisted_3 = { class: "c-download-resource__description" }
const _hoisted_4 = { class: "c-download-resource__captcha" }
const _hoisted_5 = ["src"]
const _hoisted_6 = {
  key: 0,
  class: "c-download-resource__captcha-error"
}
const _hoisted_7 = {
  key: 1,
  class: "c-download-resource__feedback"
}
const _hoisted_8 = ["disabled"]
const _hoisted_9 = {
  key: 0,
  class: "c-download-resource__submit-spinner",
  "aria-hidden": "true"
}

import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import Modal from '@/elements/Modal.vue';
import DynamicForm from '@/form/DynamicForm.vue';
import api from '@/api';
import BUS_EVENTS from '@/utils/bus-events';
import {
  DownloadResourcePayload,
  DownloadResourceProps,
} from '@/types/components/DownloadResource';
import { DynamicFormRef } from '@/types/form/dynamic-form';
import {
  applyDynamicFormAutofill,
  isCountryField,
  isStateField,
  updateRegionalFieldsByCountry,
} from '@/utils/dynamic-form-autofill';
import { getDynamicFormCheckboxDefaults } from '@/utils/dynamic-form-draft';
import { getOnlineMessageTracking } from '@/utils/online-message';


export default /*@__PURE__*/_defineComponent({
  __name: 'DownloadResource',
  props: {
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;
const bus = inject<any>('$bus');

const modalRef = ref<InstanceType<typeof Modal> | null>(null);
const dynamicFormRef = ref<DynamicFormRef>();
const activePayload = ref<DownloadResourcePayload>({});
const submitting = ref(false);
const feedbackMessage = ref('');
const captchaValue = ref('');
const captchaError = ref('');
const captchaLoading = ref(false);
const captchaImage = ref('');
const validateCode = ref('');
const codeToken = ref('');
const currentCountryValue = ref('');

const fields = computed(() => props.fields);
const activeResource = computed(() => activePayload.value.resource || {});

const getFieldValue = (
  fieldName: keyof NonNullable<DownloadResourceProps['fields']>,
  fallback = ''
) => {
  const field = fields.value?.[fieldName] as { value?: string } | undefined;
  return field?.value ?? fallback;
};

const resourceTitle = computed(() => activeResource.value.title || 'Selected resource');
const resourceDescription = computed(() => activeResource.value.description || '');
const resourceFileInfo = computed(() =>
  [activeResource.value.fileType || 'PDF', activeResource.value.fileSize]
    .filter(Boolean)
    .join(' · ')
);
const resourceIcon = computed(
  () => activeResource.value.coverImage?.src || 'data/media/images/download-resource-file.svg'
);
const resourceIconAlt = computed(
  () => activeResource.value.coverImage?.alt || resourceTitle.value || 'Resource'
);
const normalizeCaptchaData = (data: any = {}) => {
  validateCode.value = data.validateCode || data.validatecode || '';
  codeToken.value = data.codeToken || data.code_token || '';
  captchaImage.value = data.src || '';
};

const getCookie = (name: string) => {
  const item = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${name}=`));
  return item ? decodeURIComponent(item.split('=').slice(1).join('=')) : '';
};

const getSubmitHeaders = () => {
  const session = getCookie('GSAccessSession');
  return {
    Referer: window.location.href,
    ...(session ? { GSAccessSession: session } : {}),
  };
};

const getErrorMessage = (error: any, fallback: string) => {
  return error?.response?.data?.message || error?.message || error?.data?.message || fallback;
};

const refreshCaptcha = async () => {
  if (captchaLoading.value) return;

  captchaLoading.value = true;
  captchaError.value = '';
  const [data, error] = await api.common.getValidateCode();
  captchaLoading.value = false;

  if (error) {
    captchaImage.value = '';
    captchaError.value = getErrorMessage(error, 'Failed to load the security code.');
    return;
  }

  normalizeCaptchaData(data);
  captchaValue.value = '';
};

const getDownloadUrl = () =>
  activePayload.value.downloadUrl || activeResource.value.downloadLink?.href || '';

const openDownload = () => {
  const url = getDownloadUrl();
  if (!url || url === '#') return false;

  window.open(url, activeResource.value.downloadLink?.target || '_self');
  return true;
};

const getCurrentCountryValue = () => {
  const values = dynamicFormRef.value?.getValues?.() || {};
  return (
    values.country || values.countryRegion || values.countryCode || values.country_region || ''
  );
};

const autofillForm = async () => {
  await nextTick();
  await applyDynamicFormAutofill(dynamicFormRef.value);
  dynamicFormRef.value?.updateFields(getDynamicFormCheckboxDefaults(props.fields?.form));
  currentCountryValue.value = getCurrentCountryValue();
};

const onFormChange = (_fieldType: string, fieldName: string, value: string) => {
  if (isCountryField(fieldName)) {
    currentCountryValue.value = value || '';
    updateRegionalFieldsByCountry(dynamicFormRef.value, value, undefined, true);
    return;
  }

  if (isStateField(fieldName)) {
    updateRegionalFieldsByCountry(
      dynamicFormRef.value,
      currentCountryValue.value || getCurrentCountryValue(),
      value
    );
  }
};
const onOpenForm = (
  payload: DownloadResourcePayload & { onSuccess?: () => void; onFailure?: () => void }
) => {
  activePayload.value = payload || {};
  feedbackMessage.value = '';
  captchaValue.value = '';
  captchaError.value = '';
  modalRef.value?.open();
  refreshCaptcha();
  void autofillForm();
};

const onClose = () => {
  captchaValue.value = '';
  captchaError.value = '';
  modalRef.value?.close();
};

const onCaptchaInput = () => {
  captchaError.value = '';
};

const validateCaptcha = () => {
  if (captchaValue.value.trim() && codeToken.value) {
    captchaError.value = '';
    return true;
  }

  captchaError.value = 'Please enter the security code shown on the right.';
  // return false;
  return true;
};

const onSubmit = async () => {
  if (submitting.value) return;
  const validation = await dynamicFormRef.value?.validate<Record<string, unknown>>();
  if (!validation?.valid) {
    dynamicFormRef.value?.focusFirstInvalid?.();
    return;
  }
  if (!validateCaptcha()) return;

  submitting.value = true;
  feedbackMessage.value = '';
  let ex: any;
  try {
    [, ex] = await api.downloadResource.submitOnlineMessage(
      null,
      {
        ...validation.data,
        ...getOnlineMessageTracking(validation.data),
        validateCode: captchaValue.value.trim(),
        codeToken: codeToken.value,
      },
      {
        headers: getSubmitHeaders(),
      }
    );
  } catch (error) {
    ex = error;
  } finally {
    submitting.value = false;
  }

  if (ex) {
    feedbackMessage.value = getErrorMessage(
      ex,
      getFieldValue('errorMessage', 'Submission failed. Please try again.')
    );
    await refreshCaptcha();
    bus?.emit(BUS_EVENTS.FORM_SUBMIT_FAILURE, { payload: activePayload.value, error: ex });
    (activePayload.value as DownloadResourcePayload & { onFailure?: () => void }).onFailure?.();
    return;
  }

  const hasDownload = openDownload();
  feedbackMessage.value = getFieldValue(
    'successMessage',
    hasDownload ? 'Thank you. Your download will start soon.' : 'Submit success.'
  );
  dynamicFormRef.value?.resetFields();
  captchaValue.value = '';
  captchaError.value = '';
  await refreshCaptcha();
  modalRef.value?.close();
  bus?.emit(BUS_EVENTS.FORM_SUBMIT_SUCCESS, { payload: activePayload.value });
  (activePayload.value as DownloadResourcePayload & { onSuccess?: () => void }).onSuccess?.();
};

onMounted(() => {
  refreshCaptcha();
  bus?.on(BUS_EVENTS.FORM_OPEN, onOpenForm);
});

onBeforeUnmount(() => {
  bus?.off(BUS_EVENTS.FORM_OPEN, onOpenForm);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    ref_key: "modalRef",
    ref: modalRef,
    class: "c-download-resource",
    "content-class": "c-download-resource__modal",
    closable: true,
    sticky: false,
    "is-full": false
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("p", _hoisted_3, _toDisplayString(getFieldValue('description', 'Please complete the form below to access this resource.')), 1)
        ]),
        _createElementVNode("form", {
          class: "c-download-resource__form",
          onSubmit: _withModifiers(onSubmit, ["prevent"])
        }, [
          (fields.value?.form)
            ? (_openBlock(), _createBlock(DynamicForm, {
                key: 0,
                ref_key: "dynamicFormRef",
                ref: dynamicFormRef,
                form: fields.value.form,
                onChange: onFormChange
              }, null, 8, ["form"]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", {
              class: _normalizeClass(['c-download-resource__captcha-field', { 'has-error': Boolean(captchaError.value) }])
            }, [
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((captchaValue).value = $event)),
                type: "text",
                inputmode: "text",
                autocomplete: "off",
                autocapitalize: "none",
                spellcheck: "false",
                placeholder: "security code on the right",
                "aria-label": "Security code",
                onInput: onCaptchaInput
              }, null, 544), [
                [_vModelText, captchaValue.value]
              ])
            ], 2),
            _createElementVNode("img", {
              class: _normalizeClass(["c-download-resource__captcha-image", { 'is-loading': captchaLoading.value }]),
              src: captchaImage.value,
              alt: "Security code",
              role: "button",
              onClick: refreshCaptcha
            }, null, 10, _hoisted_5),
            (captchaError.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(captchaError.value), 1))
              : _createCommentVNode("", true)
          ]),
          (feedbackMessage.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(feedbackMessage.value), 1))
            : _createCommentVNode("", true),
          _createElementVNode("button", {
            class: _normalizeClass(["c-download-resource__submit", { 'is-loading': submitting.value }]),
            type: "submit",
            disabled: submitting.value
          }, [
            (submitting.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_9))
              : _createCommentVNode("", true),
            _createElementVNode("span", null, _toDisplayString(submitting.value ? 'Submitting...' : getFieldValue('submitLabel', 'Download Now')), 1)
          ], 10, _hoisted_8)
        ], 32)
      ])
    ]),
    _: 1
  }, 512))
}
}

})